import { useAuth } from '../../lib/auth.js';

/**
 * Trading desk — a gated link-out to the Kraken arbitrage bot's live dashboard.
 *
 * The bot itself runs on a dedicated low-latency server (near Kraken's matching
 * engine), NOT on Firebase — so this page does not run the engine; it authorizes
 * the operator and opens the dashboard (which has its own password) in a new tab.
 *
 * Access is restricted to an explicit allow-list of operator emails, independent
 * of SFP roles. Edit ALLOWED_EMAILS to change who can open the desk.
 */
const ALLOWED_EMAILS = new Set([
  'jshulkin@gmail.com',
  'jon@shulkinfamilypartners.com',
]);

const TRADING_URL =
  (import.meta.env.VITE_TRADING_URL as string | undefined) ?? 'http://127.0.0.1:8787';

export function TradingPortal() {
  const { user } = useAuth();
  const email = (user?.email ?? '').toLowerCase().trim();
  const allowed = ALLOWED_EMAILS.has(email);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center shadow-sm">
          <div className="text-3xl text-stone-400">⚿</div>
          <h1 className="mt-3 text-xl font-semibold text-stone-800">Trading — restricted</h1>
          <p className="mt-2 text-stone-500">
            This desk is limited to authorized operators. You're signed in as{' '}
            <span className="font-mono text-stone-700">{user?.email ?? 'unknown'}</span>, which
            isn't on the trading allow-list.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <h1 className="text-2xl font-semibold text-stone-800">Trading desk</h1>
      <p className="mt-2 text-stone-500">
        Kraken arbitrage bot — live control panel. The engine runs on a dedicated
        low-latency server; this opens its dashboard in a new tab.
      </p>

      <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-8 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-stone-400">Live dashboard</div>
            <div className="mt-1 break-all font-mono text-sm text-stone-600">{TRADING_URL}</div>
          </div>
          <a
            href={TRADING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 rounded-xl bg-stone-800 px-5 py-3 font-medium text-white transition hover:bg-stone-900"
          >
            Open dashboard ↗
          </a>
        </div>
        <p className="mt-5 text-sm text-stone-500">
          The dashboard has its own password (<span className="font-mono">DASHBOARD_PASSWORD</span>{' '}
          on the server). Real-money trading must be armed there explicitly — opening this panel
          places no orders.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { k: 'Strategy', v: 'Triangular arb (v1)' },
          { k: 'Venue', v: 'Kraken Pro' },
          { k: 'Arming', v: 'On the server only' },
        ].map((c) => (
          <div key={c.k} className="rounded-xl border border-stone-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-stone-400">{c.k}</div>
            <div className="mt-1 text-stone-700">{c.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
